import React, { useContext, useMemo } from "react";
import { Breadcrumb} from "react-bootstrap";
import { Button } from '@blueprint/ui';
import { NavLink } from "react-router-dom";

import { useInvoiceHistory } from "../../api/invoice";
import paymentApi from "../../api/payment";
import { Loader } from "../../common/components/core";
import { useSelector } from "../../redux/hooks";
import {
    currentRolesSelector,
    currentUserEmailSelector,
} from "../../redux/slices/auth/selectors";
import { ClaimStatus, BesuClaimState } from "../../types/claim";
import { UUID, HistoryItem } from "../../types/common";
import { InvoiceState, InvoiceStatus, InvoiceHistory } from "../../types/invoice";
import {
    PaymentStatus,
    PreAndPaymentStateWithDocuments,
    PaymentOrderStatus,
} from "../../types/payment";
import { BesuProjectState } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";
import { hasInvoiceAction } from "../../utils/invoice";
import {
    comparePaymentDateDesc,
    hasPreAndPaymentAction,
} from "../../utils/payment";
import { isUserFinanceDepartment } from "../../utils/role";
import ClaimContext from "../claim/ClaimContext";
import PaymentOrderDetail from "../payment/components/PaymentOrderDetail";
import PaymentReceiptDetail from "../payment/components/PaymentReceiptDetail";
import useDirectorApprovePaymentHandler from "../payment/hooks/useDirectorApprovePaymentHandler";
import useDirectorRejectPaymentHandler from "../payment/hooks/useDirectorRejectPaymentHandler";
import ProjectContext from "../project/ProjectContext";

import InvoiceDetail from "./components/InvoiceDetail";
import useContractorDiscardInvoiceHandler from "./hooks/useContractorDiscardInvoiceHandler";
import useFinanceApproveInvoiceHandler from "./hooks/useFinanceApproveInvoiceHandler";
import useFinanceRejectInvoiceHandler from "./hooks/useFinanceRejectInvoiceHandler";
import usePOApproveInvoiceHandler from "./hooks/usePOApproveInvoiceHandler";
import usePORejectInvoiceHandler from "./hooks/usePORejectInvoiceHandler";
import usePreSubmitItemHistory from "./hooks/usePreSubmitItemHistory";
import InvoiceContext from "./InvoiceContext";
import { usePaymentOrderListById } from "../../api/paymentOrder";
import useIsPreviousApproverApproved from "../payment/hooks/useIsPreviousApproverApproverd";

const InvoiceDetailView = (): JSX.Element => {
    document.title = PageTitle.InvoiceDetailView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const {
        data: claim
    } = useContext(ClaimContext);
    const {
        data: {
            state: { data: invoice },
            ref: invoiceRef,
        },
    } = useContext(InvoiceContext);
    const { data: invoiceHistory } = useInvoiceHistory(invoice.linearId.id);
    const { data: preAndPayments, isValidating } = usePaymentOrderListById(
        invoice.linearId.id,
    );

    const preSubmitPaymentIds = useMemo<string[]>(
        () =>
            preAndPayments?.map((it) => it.paymentOrderState.linearId.id) ?? [],
        [preAndPayments],
    );
    const { data: preSubmitPaymentHistoryMap } = usePreSubmitItemHistory(
        preSubmitPaymentIds,
        paymentApi.getPreSubmitHistory,
    );

    return (
        <Loader isLoading={isValidating}>
            {preAndPayments && invoiceHistory && (
                <PureInvoiceDetailView
                    project={project}
                    claim={claim}
                    invoice={invoice}
                    invoiceRef={invoiceRef}
                    preAndPayments={preAndPayments}
                    invoiceHistory={invoiceHistory}
                    preSubmitPaymentHistoryMap={preSubmitPaymentHistoryMap}
                />
            )}
        </Loader>
    );
};

type PureInvoiceDetailViewProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    invoice: InvoiceState;
    invoiceRef: string;
    preAndPayments: PreAndPaymentStateWithDocuments[];
    invoiceHistory: InvoiceHistory;
    preSubmitPaymentHistoryMap: Map<UUID, HistoryItem<PaymentOrderStatus>[]>;
};

export const PureInvoiceDetailView = ({
    project,
    claim,
    invoice,
    invoiceRef,
    preAndPayments,
    invoiceHistory,
    preSubmitPaymentHistoryMap,
}: PureInvoiceDetailViewProps): JSX.Element => {
    const { handler: contractorDiscard } =
        useContractorDiscardInvoiceHandler(invoiceRef);
    const { handler: poApprove } = usePOApproveInvoiceHandler(invoiceRef);
    const { handler: poReject } = usePORejectInvoiceHandler(invoiceRef);
    const { handler: financeApprove } = useFinanceApproveInvoiceHandler(
        project,
        claim,
        invoice,
        invoiceRef,
    );
    const { handler: financeReject } =
        useFinanceRejectInvoiceHandler(invoiceRef);
    const { handler: directorApprove } = useDirectorApprovePaymentHandler();
    const { handler: directorReject } = useDirectorRejectPaymentHandler();
    const currentRoles = useSelector(currentRolesSelector);
    const currentUserEmail = useSelector(currentUserEmailSelector);
    const isDiscarded = claim.status === ClaimStatus.DISCARDED;
    const sortedPayments = [...preAndPayments].sort(comparePaymentDateDesc);
    const currentPaymentApprover = useMemo(() => {
        return sortedPayments[0]?.paymentOrderState.props.approvers.find(
            (it) => it.details.email === currentUserEmail,
        );
    }, [currentUserEmail, sortedPayments]);

    const isPreviousPriorityApproverApproved = useIsPreviousApproverApproved(
        sortedPayments[0]?.paymentOrderState.props.approvers ?? [],
    );

    const hasPaymentAction = hasPreAndPaymentAction(
        currentRoles,
        sortedPayments[0]?.paymentOrderState.status,
        sortedPayments[0]?.paymentState?.status,
        invoice.status,
        currentPaymentApprover,
        isPreviousPriorityApproverApproved,
    );
    const isPaymentRejected =
        invoice.status === InvoiceStatus.FINANCE_APPROVED &&
        (sortedPayments[0]?.paymentOrderState.status ===
            PaymentOrderStatus.REJECTED ||
            sortedPayments[0]?.paymentState?.status === PaymentStatus.FAILED);

    const isPaymentApproverActionEnabled: boolean = useMemo(() => {
        const isFirstApprover = isPreviousPriorityApproverApproved === null;

        return (
            invoice.status === InvoiceStatus.FINANCE_APPROVED &&
            (sortedPayments[0]?.paymentOrderState.status ===
                PaymentOrderStatus.CREATED ||
                sortedPayments[0]?.paymentOrderState.status ===
                    PaymentOrderStatus.PARTIAL_APPROVED) &&
            hasPaymentAction &&
            (isFirstApprover || !!isPreviousPriorityApproverApproved)
        );
    }, [
        hasPaymentAction,
        invoice.status,
        sortedPayments,
        isPreviousPriorityApproverApproved,
    ]);

    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink
                        to={`/project/${project.referenceId}/claim/${claim.referenceId}`}
                    >
                        Claim {shortenUUID(claim.referenceId)}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    Invoice {shortenUUID(invoice.linearId.id)}
                </Breadcrumb.Item>
            </Breadcrumb>

            <div
                className={`d-flex align-items-center mb-3 ${
                    isDiscarded ? "text-placeholder-grey" : ""
                }`}
            >
                <h1 className="flex-grow-1 mb-0">Invoice</h1>
                {(invoice.status === InvoiceStatus.PO_REJECTED ||
                    invoice.status === InvoiceStatus.FINANCE_REJECTED) &&
                    hasInvoiceAction(currentRoles, invoice.status) && (
                        <>
                            <Button
                                variant="light.primary"
                                onClick={contractorDiscard}
                            >
                                Discard
                            </Button>
                            <NavLink
                                to={`/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}/edit`}
                            >
                                <Button variant="light.primary" className="ms-4">
                                    Edit Invoice
                                </Button>
                            </NavLink>
                        </>
                    )}
                {invoice.status === InvoiceStatus.CONTRACTOR_SUBMITTED &&
                    hasInvoiceAction(currentRoles, invoice.status) && (
                        <>
                            <Button variant="light.primary" onClick={poReject}>
                                Reject
                            </Button>
                            <Button variant="light.primary" onClick={poApprove} className="ms-4">
                                Approve
                            </Button>
                        </>
                    )}
                {((invoice.status === InvoiceStatus.PO_APPROVED &&
                    hasInvoiceAction(currentRoles, invoice.status)) ||
                    (invoice.status === InvoiceStatus.FINANCE_APPROVED &&
                        ((!sortedPayments[0] &&
                            isUserFinanceDepartment(currentRoles)) ||
                            (isPaymentRejected && hasPaymentAction)))) && (
                    <>
                        <Button variant="light.primary" onClick={financeReject}>
                            Reject
                        </Button>
                        <Button variant="light.primary" onClick={financeApprove} className="ms-4">
                            Approve
                        </Button>
                    </>
                )}
                {isPaymentApproverActionEnabled && (
                    <>
                        <Button
                            variant="light.primary"
                            onClick={() =>
                                directorReject(
                                    sortedPayments[0].paymentOrderState.linearId
                                        .id,
                                )
                            }
                        >
                            Reject
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={() =>
                                directorApprove(
                                    sortedPayments[0].paymentOrderState.linearId
                                        .id,
                                )
                            }
                            className="ms-4"
                        >
                            Approve
                        </Button>
                    </>
                )}
            </div>

            {invoice.status === InvoiceStatus.FINANCE_APPROVED &&
            sortedPayments[0] &&
            sortedPayments[0].paymentState?.status === PaymentStatus.FAILED ? (
                // When paymentStatus === FAILED , invoice details will be in the first position
                <>
                    <InvoiceDetail
                        invoice={invoice}
                        invoiceHistory={invoiceHistory}
                        isPaymentRejected={isPaymentRejected}
                        hasPaymentAction={hasPaymentAction}
                    />
                    <PaymentAndReceipt
                        preAndPayment={sortedPayments[0]}
                        preSubmitPaymentHistory={
                            preSubmitPaymentHistoryMap?.get(sortedPayments[0].paymentOrderState.linearId.id) ?? []
                        }
                        closed={false}
                    />
                </>
            ) : (
                <>
                    {invoice.status === InvoiceStatus.FINANCE_APPROVED &&
                        sortedPayments[0] && (
                            <PaymentAndReceipt
                                preAndPayment={sortedPayments[0]}
                                preSubmitPaymentHistory={
                                    preSubmitPaymentHistoryMap?.get(sortedPayments[0].paymentOrderState.linearId.id) ?? []
                                }
                                closed={false}
                            />
                        )}

                    <InvoiceDetail
                        invoice={invoice}
                        invoiceHistory={invoiceHistory}
                        isPaymentRejected={isPaymentRejected}
                        hasPaymentAction={hasPaymentAction}
                    />
                </>
            )}


            {(invoice.status === InvoiceStatus.FINANCE_APPROVED
                ? sortedPayments.slice(1)
                : sortedPayments
            ).map((preAndPayment) => (
                <PaymentAndReceipt
                    key={preAndPayment.paymentOrderState.linearId.id}
                    preAndPayment={preAndPayment}
                    preSubmitPaymentHistory={
                        preSubmitPaymentHistoryMap?.get(sortedPayments[0].paymentOrderState.linearId.id) ?? []
                    }
                    closed={true}
                />
            ))}
        </>
    );
};

type PaymentAndReceiptProps = {
    preAndPayment: PreAndPaymentStateWithDocuments;
    preSubmitPaymentHistory: HistoryItem<PaymentOrderStatus>[];
    closed: boolean;
};

const PaymentAndReceipt = ({
    preAndPayment,
    preSubmitPaymentHistory,
    closed,
}: PaymentAndReceiptProps): JSX.Element => {
    return (
        <>
            {(preAndPayment.paymentState?.status === PaymentStatus.COMPLETED ||
                preAndPayment.paymentState?.status ===
                    PaymentStatus.FAILED) && (
                <PaymentReceiptDetail preAndPayment={preAndPayment} />
            )}
            <PaymentOrderDetail
                preAndPayment={preAndPayment}
                preSubmitPaymentHistory={preSubmitPaymentHistory}
                closed={closed}
            />
        </>
    );

};

export default InvoiceDetailView;
